import { ApiError, apiInstance, configureLogger, logger } from '@multiplatform-apps/service-trips';
import { useEffect } from 'react';

interface NewRelicGlobal {
  noticeError: (error: unknown, customAttributes?: Record<string, unknown>) => void;
}

// Renders a non-Error value (e.g. a raw response body ServiceTripsApi.request()
// can throw directly) into a short diagnostic string for the Error message.
const describeNonError = (value: unknown): string => {
  if (value === undefined) return 'no error object provided';
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const getNewRelic = (): NewRelicGlobal | undefined =>
  (window as unknown as { newrelic?: NewRelicGlobal }).newrelic;

// The agent is loaded through a dynamic import, so the earliest errors — most
// importantly a crash in the provider stack that `ErrorBoundary` wraps in
// main.tsx — are reported before `window.newrelic` exists and would otherwise
// be dropped. Buffer those and flush once the agent is up. Bounded, so an
// agent that never loads (missing env vars, blocked chunk) can't leak memory.
const MAX_BUFFERED_REPORTS = 25;
const bufferedReports: Array<[Error, Record<string, unknown>]> = [];

const noticeError = (error: Error, attributes: Record<string, unknown>): void => {
  const newRelic = getNewRelic();
  if (newRelic) {
    newRelic.noticeError(error, attributes);
    return;
  }
  if (bufferedReports.length < MAX_BUFFERED_REPORTS) {
    bufferedReports.push([error, attributes]);
  }
};

const flushBufferedReports = (): void => {
  const newRelic = getNewRelic();
  if (!newRelic) return;
  bufferedReports.splice(0).forEach(([error, attributes]) => {
    newRelic.noticeError(error, attributes);
  });
};

// ENB-2331: route logger.error to the New Relic browser agent in production,
// and report every failed API request (native already does the equivalent for
// Crashlytics via api-context.tsx). Deferred to `initNewRelic()` (not module
// scope) so merely importing this file/the hooks barrel — as many spec files
// that mock `@multiplatform-apps/service-trips` do transitively — can't crash
// on a partial mock missing `apiInstance`/`configureLogger`.
let reportingConfigured = false;

const configureErrorReporting = (): void => {
  if (reportingConfigured) return;
  reportingConfigured = true;

  // `error` isn't guaranteed to be an Error instance — ServiceTripsApi.request()
  // can throw a raw response body (e.g. a validation-errors object) directly —
  // so noticeError would otherwise receive a non-Error, causing the report to
  // be rejected or lose its stack/message. Normalize to a real Error first.
  configureLogger((message, error, context) => {
    const reported = error instanceof Error ? error : new Error(`${message}: ${describeNonError(error)}`);
    noticeError(reported, { message, ...context });
  });

  // 401s are expected and silently recovered by apiInstance's token-refresh
  // retry — reporting them would flood New Relic with a "failure" on every
  // routine token expiry, so they're excluded here.
  apiInstance.setInterceptors(
    (_url, init) => init,
    (error) => {
      if (error instanceof ApiError && error.status === 401) return;
      logger.error('API request failed', error);
    }
  );
};

const LICENSE_KEY = process.env['NX_PUBLIC_NEW_RELIC_LICENSE_KEY'];
const APP_ID = process.env['NX_PUBLIC_NEW_RELIC_APP_ID'];
const ACCOUNT_ID = process.env['NX_PUBLIC_NEW_RELIC_ACCOUNT_ID'];
const TRUST_KEY = process.env['NX_PUBLIC_NEW_RELIC_TRUST_KEY'];

const BEACON = 'bam.eu01.nr-data.net';

// A transient failure (network blip, chunk blocked by an ad-blocker/proxy) is
// retried a bounded number of times with backoff. `initNewRelic()` runs once
// per page load, and client-side navigation via RouterProvider never re-runs
// it — so without an explicit retry loop here, a transient failure would
// disable the agent for the rest of the session with no way to recover short
// of a full page reload.
const MAX_INIT_ATTEMPTS = 3;
const RETRY_DELAY_MS = 5000;

// Module-level guard: the BrowserAgent cannot be torn down once initialized.
let initialized = false;

const isInitializable = (): boolean => !initialized && Boolean(LICENSE_KEY) && Boolean(APP_ID);

// Avoids flooding the network tab with React dev-mode `measure()` calls; real users never hit these hosts.
const isLocalhost = (): boolean => {
  const { hostname } = window.location;
  // `window.location.hostname` for an IPv6 loopback URL (e.g. http://[::1]:4200) is
  // the bracketed literal "[::1]", not "::1" — match both forms.
  return hostname === 'localhost' || hostname === '::1' || hostname === '[::1]' || hostname.startsWith('127.');
};

const initAgent = async (attempt = 1): Promise<void> => {
  if (!isInitializable()) return;
  // Set synchronously, before the async import below, so two calls racing
  // before the first one resolves can't both pass the guard and construct
  // two BrowserAgent instances (the SDK can't be torn down once initialized).
  initialized = true;

  try {
    const { BrowserAgent } = await import(
      '@newrelic/browser-agent/loaders/browser-agent'
    );

    new BrowserAgent({
      init: {
        distributed_tracing: { enabled: true },
        performance: { capture_measures: !isLocalhost() },
        browser_consent_mode: { enabled: false },
        privacy: { cookies_enabled: true },
        ajax: { enabled: false },
        session_trace: { enabled: false },
      },
      info: {
        beacon: BEACON,
        errorBeacon: BEACON,
        licenseKey: LICENSE_KEY!,
        applicationID: APP_ID!,
        sa: 1,
      },
      loader_config: {
        ...(ACCOUNT_ID && { accountID: ACCOUNT_ID }),
        ...(TRUST_KEY && { trustKey: TRUST_KEY }),
        agentID: APP_ID!,
        licenseKey: LICENSE_KEY!,
        applicationID: APP_ID,
      },
    });

    flushBufferedReports();
  } catch {
    initialized = false;
    if (attempt < MAX_INIT_ATTEMPTS) {
      setTimeout(() => {
        void initAgent(attempt + 1);
      }, RETRY_DELAY_MS * attempt);
    }
  }
};

/**
 * ENB-2428: New Relic is treated as necessary tracking (error monitoring) and
 * initialized unconditionally — no cookie consent gate. See the cookie banner +
 * privacy policy for user-facing disclosure.
 *
 * Called from main.tsx before the React tree renders, so a crash anywhere in
 * the provider stack is reportable. Idempotent — the hook below calls it again.
 */
export const initNewRelic = (): void => {
  configureErrorReporting();
  void initAgent();
};

export const useNewRelic = (): void => {
  useEffect(() => {
    initNewRelic();
  }, []);
};
